/**
 * useChatPanelOpen Hook
 *
 * Persists the desktop chat right-panel open/closed state in localStorage and
 * keeps every mounted instance in sync via a same-tab CustomEvent. Without the
 * event, the Dashboard's instance never sees a write from (e.g.) the Getting
 * Started checklist, so its "Open chat" button does nothing visible.
 *
 * Defaults to closed on first load.
 */

import { useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'danny_chat_panel_open';
const CHANGE_EVENT = 'danny:chat-panel:changed';

function persistAndBroadcast(next: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
  } catch {
    // localStorage may be blocked (private mode, etc.) — ignore
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  }
}

export function useChatPanelOpen() {
  const [isOpen, setIsOpenState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  // Track the latest value so `toggle` can compute the next state WITHOUT
  // reading from inside a state-updater function. Broadcasting (a side effect)
  // from within an updater runs during React's render phase, which then calls
  // setState on every other mounted instance mid-render — the "Cannot update a
  // component while rendering a different component" warning. Keeping the side
  // effect in the event-handler call stack (below) avoids that entirely.
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Keep this instance in sync when ANY other instance writes the state —
  // both same-tab (custom event) and cross-tab (storage event).
  useEffect(() => {
    const onCustom = (e: Event) => {
      const next = (e as CustomEvent<boolean>).detail;
      if (typeof next === 'boolean') setIsOpenState(next);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setIsOpenState(e.newValue === 'true');
    };
    window.addEventListener(CHANGE_EVENT, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setOpen = useCallback((next: boolean) => {
    setIsOpenState(next);
    persistAndBroadcast(next);
  }, []);

  const toggle = useCallback(() => {
    const next = !isOpenRef.current;
    setIsOpenState(next);
    persistAndBroadcast(next);
  }, []);

  return { isOpen, setOpen, toggle };
}
